import { ActionReducer, MetaReducer } from "@ngrx/store";
import { storeFreeze } from "ngrx-store-freeze";

import { environment } from "../../../environments/environment";
import {
  ApiKeyEffects,
  apiKeyReducer,
  ApiKeyService,
  ApiKeyState,
} from "./api-key";
import {
  AuthenticationEffects,
  authenticationReducer,
  AuthenticationState,
} from "./authentication";
import {
  DocumentEffects,
  documentReducer,
  DocumentService,
  DocumentState,
} from "./document";
import {
  FeatureEffects,
  featureReducer,
  FeatureService,
  FeatureState,
} from "./feature";
import { loaderReducer, LoaderState } from "./loader";
import {
  MetricsEffects,
  metricsReducer,
  MetricsService,
  MetricsState,
} from "./metrics";
import {
  OwnerFeatureFlagsEffects,
  ownerFeatureFlagsReducer,
  OwnerFeatureFlagsService,
  OwnerFeatureFlagsState,
} from "./owner-feature-flags";
import {
  ProductEffects,
  productReducer,
  ProductService,
  ProductState,
} from "./product";
import {
  ProductFeatureEffects,
  productFeatureReducer,
  ProductFeatureService,
  ProductFeatureState,
} from "./product-feature";
import { RegionEffects } from "./region/region.effects";
import { RegionState } from "./region/region.model";
import { regionReducer } from "./region/region.reducer";
import { RegionService } from "./region/region.service";
import {
  RequestEffects,
  requestReducer,
  RequestService,
  RequestState,
} from "./request";
import { TelemetryEffects } from "./telemetry";
import { UserEffects, userReducer, UserService, UserState } from "./user";

export interface AppState {
  request: RequestState;
  document: DocumentState;
  apiKey: ApiKeyState;
  user: UserState;
  product: ProductState;
  loader: LoaderState;
  authentication: AuthenticationState;
  region: RegionState;
  metrics: MetricsState;
  productFeature: ProductFeatureState;
  feature: FeatureState;
  ownerFeatureFlags: OwnerFeatureFlagsState;
}

export interface AppStore {
  request: ActionReducer<RequestState>;
  document: ActionReducer<DocumentState>;
  apiKey: ActionReducer<ApiKeyState>;
  user: ActionReducer<UserState>;
  product: ActionReducer<ProductState>;
  loader: ActionReducer<LoaderState>;
  authentication: ActionReducer<AuthenticationState>;
  region?: ActionReducer<RegionState>;
  metrics: ActionReducer<MetricsState>;
  productFeature: ActionReducer<ProductFeatureState>;
  feature: ActionReducer<FeatureState>;
  ownerFeatureFlags: ActionReducer<OwnerFeatureFlagsState>;
}

export const appReducers: AppStore = {
  request: requestReducer,
  apiKey: apiKeyReducer,
  document: documentReducer,
  user: userReducer,
  product: productReducer,
  loader: loaderReducer,
  authentication: authenticationReducer,
  productFeature: productFeatureReducer,
  feature: featureReducer,
  region: regionReducer,
  metrics: metricsReducer,
  ownerFeatureFlags: ownerFeatureFlagsReducer,
};

export const appEffects = [
  RequestEffects,
  ApiKeyEffects,
  DocumentEffects,
  UserEffects,
  ProductEffects,
  AuthenticationEffects,
  TelemetryEffects,
  RegionEffects,
  MetricsEffects,
  ProductFeatureEffects,
  FeatureEffects,
  OwnerFeatureFlagsEffects,
];

export const apiServices = [
  RequestService,
  ApiKeyService,
  DocumentService,
  UserService,
  ProductService,
  RegionService,
  MetricsService,
  ProductFeatureService,
  FeatureService,
  OwnerFeatureFlagsService,
];

export const metaReducers: MetaReducer<AppState>[] = !environment.production
  ? [storeFreeze]
  : [];
